import {createSelector} from 'reselect';
import {
  getLocalOverridePluginIdBySlug,
  getInstalledRealPluginIdBySlug,
  getLiveLocalDevOverrideMatches,
  getLiveLocalDevPluginId,
  resolveLocalDevServerPort,
  type LocalDevServerStatus,
} from '@packages/systems/code-libraries/core';
import {type DesignerStores} from '@packages/systems/designer/flux';
import * as persistentUIState from '@packages/systems/designer/persistent-state';
import {initialCodeComponentUIState} from './CodeComponentUIState';

/**
 * The local-dev server's origin, on the port set in site settings or the default.
 */
export function getLocalDevServerOrigin(): string {
  const port = resolveLocalDevServerPort(
    persistentUIState.get().localDevServerPort
  );
  return `http://localhost:${port}`;
}

const selectLibraries = (state: DesignerStores) =>
  state.DesignerStore.libraries;

/**
 * Whether the site has a library with code components that a local override
 * could replace. A local override does not count, since it replaces something.
 */
export const selectHasInstalledCodeComponents = createSelector(
  selectLibraries,
  (libraries) =>
    Object.values(libraries).some(
      (library) => !library.isLocalOverride && library.codeComponentsCount > 0
    )
);

/**
 * Whether installed libraries have finished loading, so no match means "not
 * installed" rather than "not loaded yet".
 */
export const selectInstalledLibrariesSettled = (state: DesignerStores) =>
  state.DesignerStore.availableLibraries.installedResourcesLoaded &&
  (state.DesignerStore.dynamicPluginsManager.status === 'IDLE' ||
    state.DesignerStore.dynamicPluginsManager.status === 'FINISHED');

// Read with a fallback to the initial state, as other readers of this store do:
// a state that has no code component UI store has nothing connected.

/**
 * Whether the local-dev server is reachable; written by LocalDevOverrideSync.
 */
export const selectLocalDevServerReachable = (state: DesignerStores) =>
  state.CodeComponentUIStore?.localDevServerReachable ??
  initialCodeComponentUIState.localDevServerReachable;

/**
 * The library the dev server is serving, or null.
 */
export const selectLocalDevDiscoveredDescriptor = (state: DesignerStores) =>
  state.CodeComponentUIStore?.localDevDiscoveredDescriptor ??
  initialCodeComponentUIState.localDevDiscoveredDescriptor;

/**
 * The libraries (by slug) whose local override the user switched off.
 */
export const selectLocalOverrideOptedOutSlugs = (state: DesignerStores) =>
  state.CodeComponentUIStore?.localOverrideOptedOutSlugs ??
  initialCodeComponentUIState.localOverrideOptedOutSlugs;

/** slug -> pluginId, for every local-dev override currently added. */
export const selectLocalOverrideIdBySlug = createSelector(
  selectLibraries,
  getLocalOverridePluginIdBySlug
);

/** slug -> pluginId, for every currently-installed REAL (non-local-dev) library. */
export const selectInstalledRealLibraryIdBySlug = createSelector(
  selectLibraries,
  getInstalledRealPluginIdBySlug
);

const selectLocalDevServerStatus = createSelector(
  selectLocalDevServerReachable,
  selectLocalDevDiscoveredDescriptor,
  (isReachable, descriptor): LocalDevServerStatus => ({
    isReachable,
    discoveredLibrarySlug: descriptor?.librarySlug,
  })
);

/**
 * The local override's library the dev server is serving right now. See
 * `getLiveLocalDevPluginId`, the one definition of a live override.
 */
export const selectLiveLocalDevPluginId = createSelector(
  selectLibraries,
  selectLocalDevServerStatus,
  getLiveLocalDevPluginId
);

/** The real/local component pairs whose override is live right now. */
export const selectLiveLocalDevOverrideMatches = createSelector(
  selectLibraries,
  selectLocalDevServerStatus,
  getLiveLocalDevOverrideMatches
);
